import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CompanySetting, CompanySettingValue } from '@/api/companies/api';
import { CompanySettings } from '@/types/company-settings';
import { FormState } from '@/common/types/form';
import { FieldsStateType } from '@/common/types/form';
import { formatDisplayValue, valuesEqual } from './change-tracking';
import { SearchableSetting, SettingChange, useCompanySettingsDraft } from './draft-context';

type PayloadItem = NonNullable<CompanySetting>;

type CompanySettingFieldValue = CompanySettings[keyof CompanySettings];

type CategoryDraftWatch<T> = FieldsStateType<T> | readonly [FieldsStateType<T>, FormState[]];

type UseCategoryDraftParams<T> = {
  categoryId: string;
  categoryTitle: string;
  companySettings: CompanySettings | undefined;
  initializeForm: () => void;
  collectValues: (validate?: boolean) => T | null;
  toPayload: (values: T) => (PayloadItem | null)[];
  labels: Record<string, string>;
  formatValue?: (key: string, value: CompanySettingFieldValue | CompanySettingValue) => string;
  watch: CategoryDraftWatch<T>;
  validate?: (values: T) => boolean;
};

const snapshotWatch = <T>(watch: CategoryDraftWatch<T>) => {
  try {
    return JSON.stringify(watch);
  } catch {
    return String(watch);
  }
};

export const useCategoryDraft = <T>({
  categoryId,
  categoryTitle,
  companySettings,
  initializeForm,
  collectValues,
  toPayload,
  labels,
  formatValue = (key, value) => {
    void key;
    return formatDisplayValue(value);
  },
  watch,
  validate
}: UseCategoryDraftParams<T>) => {
  const { reportCategoryChanges, reportSearchables, registerCategory } = useCompanySettingsDraft();
  const [formEpoch, setFormEpoch] = useState(0);
  const lastAppliedEpoch = useRef(0);
  const paramsRef = useRef({
    companySettings,
    collectValues,
    toPayload,
    labels,
    formatValue,
    validate,
    categoryTitle
  });

  paramsRef.current = {
    companySettings,
    collectValues,
    toPayload,
    labels,
    formatValue,
    validate,
    categoryTitle
  };

  const valuesSnapshot = useMemo(() => snapshotWatch(watch), [watch]);

  const wrappedInit = useCallback(() => {
    initializeForm();
    setFormEpoch(current => current + 1);
  }, [initializeForm]);

  useEffect(() => {
    wrappedInit();
  }, [wrappedInit]);

  useEffect(() => {
    const {
      companySettings: original,
      collectValues: collect,
      toPayload: mapPayload,
      labels: fieldLabels,
      formatValue: format,
      categoryTitle: title
    } = paramsRef.current;

    if (!original) {
      reportCategoryChanges(categoryId, []);
      reportSearchables(categoryId, []);
      return;
    }

    if (formEpoch === 0) return;

    if (lastAppliedEpoch.current !== formEpoch) {
      lastAppliedEpoch.current = formEpoch;
      reportCategoryChanges(categoryId, []);

      const hydratedValues = collect(false);
      if (hydratedValues) {
        const hydratedPayload = mapPayload(hydratedValues).filter(
          (item): item is PayloadItem => item != null
        );
        reportSearchables(
          categoryId,
          hydratedPayload.map(item => ({
            key: item.key,
            label: fieldLabels[item.key] || item.key,
            valueText: format(item.key, item.value)
          }))
        );
      }
      return;
    }

    const values = collect(false);
    if (!values) {
      reportCategoryChanges(categoryId, []);
      return;
    }

    const payload = mapPayload(values).filter((item): item is PayloadItem => item != null);
    const searchables: SearchableSetting[] = payload.map(item => ({
      key: item.key,
      label: fieldLabels[item.key] || item.key,
      valueText: format(item.key, item.value)
    }));
    reportSearchables(categoryId, searchables);

    const nextChanges: SettingChange[] = payload
      .filter(item => !valuesEqual(item.value, original[item.key as keyof CompanySettings]))
      .map(item => ({
        key: item.key,
        categoryId,
        categoryTitle: title,
        label: fieldLabels[item.key] || item.key,
        oldDisplay: format(item.key, original[item.key as keyof CompanySettings]),
        newDisplay: format(item.key, item.value),
        payload: item
      }));

    reportCategoryChanges(categoryId, nextChanges);
  }, [categoryId, formEpoch, reportCategoryChanges, reportSearchables, valuesSnapshot]);

  useEffect(() => {
    return registerCategory(categoryId, {
      reset: wrappedInit,
      validate: () => {
        const { collectValues: collect, validate: runValidate } = paramsRef.current;
        const values = collect(true);
        if (!values) return false;
        return runValidate ? runValidate(values) : true;
      }
    });
  }, [categoryId, registerCategory, wrappedInit]);
};
